/**
 * Shared reader for connected-apps ``connect_required`` tool errors and
 * the same-turn connect HITL wait (approval_kind ``connect``).
 */

import type { ChatItem, ToolUseItem } from '../../../contracts/harness.types';
import { isRecord } from '../../../contracts/parse-utils';

export const CONNECT_APPROVAL_KIND = 'connect';
export const CONNECT_WAIT_ID_PREFIX = 'connect-';

export interface ConnectRequiredPayload {
  kind: string;
  connectors: string[];
  stack?: 'nango' | 'merge';
}

const readToolData = (item: ToolUseItem): Record<string, unknown> | undefined => {
  const data = item.result_provider_metadata?.data;
  return isRecord(data) ? data : undefined;
};

const parseConnectPayload = (data: unknown): ConnectRequiredPayload | null => {
  if (!isRecord(data) || data.code !== 'connect_required') return null;
  const kind = typeof data.kind === 'string' ? data.kind : 'app';
  const raw = data.connectors;
  const connectors = Array.isArray(raw)
    ? raw.filter((c): c is string => typeof c === 'string' && c.length > 0)
    : [];
  const stack = data.stack === 'nango' || data.stack === 'merge' ? data.stack : undefined;
  return { kind, connectors, stack };
};

export const readConnectRequired = (item: ToolUseItem): ConnectRequiredPayload | null => {
  const fromResult = parseConnectPayload(readToolData(item));
  if (fromResult) return fromResult;
  return (
    parseConnectPayload(item.approval?.context?.connect_required) ??
    parseConnectPayload(item.pendingApprovalContext?.connect_required)
  );
};

export const isConnectRequiredData = (
  data: Record<string, unknown> | undefined
): data is Record<string, unknown> & { code: 'connect_required' } =>
  isRecord(data) && data.code === 'connect_required';

export const isConnectWaitItem = (item: ToolUseItem): boolean => {
  if (item.pendingApprovalKind === CONNECT_APPROVAL_KIND) return true;
  if (item.pendingApprovalId?.startsWith(CONNECT_WAIT_ID_PREFIX)) return true;
  if (
    item.status === 'pending_approval' &&
    item.approval?.approval_kind === CONNECT_APPROVAL_KIND
  ) {
    return true;
  }
  if (
    item.approval?.approval_kind === CONNECT_APPROVAL_KIND &&
    item.approval.status !== 'pending' &&
    item.status !== 'complete' &&
    item.status !== 'pending_approval'
  ) {
    return true;
  }
  return false;
};

/**
 * True when this assistant bubble follows a connect wait or a
 * ``connect_required`` tool in the same user turn.
 */
export const turnHasConnectRequiredBefore = (
  items: readonly ChatItem[] | undefined,
  currentId: string
): boolean => {
  if (!items?.length) return false;
  const currentIndex = items.findIndex((item) => item.id === currentId);
  if (currentIndex <= 0) return false;
  for (let i = currentIndex - 1; i >= 0; i -= 1) {
    const item = items[i];
    if (!item) continue;
    if (item.kind === 'user_message') return false;
    if (
      item.kind === 'tool_use' &&
      (isConnectWaitItem(item) || readConnectRequired(item) !== null)
    ) {
      return true;
    }
  }
  return false;
};
